import { Row, Col, Card, Typography, Button, Space, Tag } from 'antd';
import { Check } from 'lucide-react';
import { motion } from 'motion/react';

const { Title, Paragraph, Text } = Typography;

export function Hero() {
  return (
    <section className="hero-section">
      {/* Background Image with Overlay */}
      <div className="hero-bg">
        <img
          src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Background"
        />
      </div>

      <div
        className="hero-content"
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 24px',
          width: '100%',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            maxWidth: 800,
            margin: '0 auto 64px auto',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Title
              level={1}
              style={{
                fontSize: 'clamp(36px, 5vw, 56px)',
                color: '#fff',
                marginBottom: '24px',
                lineHeight: 1.2,
                fontWeight: 700,
              }}
            >
              Launch Subscription Plans in Hours, not Days
            </Title>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Paragraph
              style={{
                fontSize: 'clamp(16px, 2.5vw, 20px)',
                color: 'rgba(255, 255, 255, 0.85)',
                marginBottom: '40px',
                lineHeight: 1.6,
              }}
            >
              The complete subscription infrastructure for SaaS companies.
              Plans, features, entitlements, API limits, trials, billing
              integrations, tenant management, and customer self-service — all
              in one platform.
            </Paragraph>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Space size="middle" wrap style={{ justifyContent: 'center' }}>
              <Button
                type="primary"
                size="large"
                href="/login"
                style={{
                  height: 48,
                  padding: '0 32px',
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                Start Free Trial
              </Button>
              <Button
                size="large"
                href="#demo"
                style={{
                  height: 48,
                  padding: '0 32px',
                  fontSize: 16,
                  fontWeight: 600,
                  background: '#fff',
                  border: '1px solid #d9d9d9',
                  color: '#000',
                }}
              >
                Book Demo
              </Button>
            </Space>
          </motion.div>
        </div>

        {/* Hero Visual - Dashboard Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Card
            variant="borderless"
            style={{
              maxWidth: 1000,
              margin: '0 auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              borderRadius: '16px',
            }}
          >
            <Row gutter={[24, 24]}>
              {/* Plans Panel */}
              <Col xs={24} md={8}>
                <Text
                  type="secondary"
                  strong
                  style={{
                    fontSize: '12px',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '16px',
                  }}
                >
                  SUBSCRIPTION PLANS
                </Text>
                <Space
                  orientation="vertical"
                  size="middle"
                  style={{ width: '100%' }}
                >
                  {['Basic Plan', 'Professional Plan', 'Enterprise Plan'].map(
                    (plan, i) => (
                      <div
                        key={plan}
                        style={{
                          padding: '16px',
                          background:
                            'linear-gradient(135deg, #f0f5ff 0%, #f9f0ff 100%)',
                          borderRadius: '8px',
                          border: '1px solid #adc6ff',
                        }}
                      >
                        <Text
                          strong
                          style={{ color: '#141414', display: 'block' }}
                        >
                          {plan}
                        </Text>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: '13px',
                            marginTop: '4px',
                            display: 'block',
                          }}
                        >
                          ${[29, 99, 299][i]}/mo
                        </Text>
                      </div>
                    ),
                  )}
                </Space>
              </Col>

              {/* Feature Matrix */}
              <Col xs={24} md={8}>
                <Text
                  type="secondary"
                  strong
                  style={{
                    fontSize: '12px',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '16px',
                  }}
                >
                  FEATURE MATRIX
                </Text>
                <Space
                  orientation="vertical"
                  size="small"
                  style={{ width: '100%' }}
                >
                  {[
                    'Analytics',
                    'API Access',
                    'Team Members',
                    'Priority Support',
                  ].map((feature) => (
                    <div
                      key={feature}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px',
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0',
                      }}
                    >
                      <Check size={16} style={{ color: '#389e0d' }} />
                      <Text style={{ fontSize: '14px', color: '#262626' }}>
                        {feature}
                      </Text>
                    </div>
                  ))}
                </Space>
              </Col>

              {/* Usage Metrics */}
              <Col xs={24} md={8}>
                <Text
                  type="secondary"
                  strong
                  style={{
                    fontSize: '12px',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '16px',
                  }}
                >
                  USAGE METRICS
                </Text>
                <Space
                  orientation="vertical"
                  size="small"
                  style={{ width: '100%' }}
                >
                  {[
                    { label: 'API Requests', value: '2.4M' },
                    { label: 'Subscriptions', value: '1,247' },
                    { label: 'Revenue', value: '$94.2K' },
                    { label: 'Active Tenants', value: '342' },
                  ].map((metric) => (
                    <div
                      key={metric.label}
                      style={{
                        padding: '12px',
                        background:
                          'linear-gradient(90deg, #1677ff 0%, #722ed1 100%)',
                        borderRadius: '8px',
                        color: '#ffffff',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          lineHeight: 1.2,
                        }}
                      >
                        {metric.value}
                      </div>
                      <div style={{ fontSize: '11px', opacity: 0.9 }}>
                        {metric.label}
                      </div>
                    </div>
                  ))}
                </Space>
              </Col>
            </Row>

            {/* Floating tags */}
            <div style={{ marginTop: '24px' }}>
              <Space size="small" wrap>
                {[
                  'API Keys',
                  'Entitlements',
                  'Usage Limits',
                  'Feature Flags',
                ].map((tag) => (
                  <Tag
                    key={tag}
                    variant="filled"
                    style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      background: '#f5f5f5',
                      color: '#595959',
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
              </Space>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
